import { HttpClient, HttpContext, HttpEventType, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, filter, map, Observable, of, ReplaySubject, scan } from 'rxjs';
import { environment } from '../../environments/environment';
import { BYPASS_SPINNER } from '../../utility/spinner/spinner.interceptor';
import { Reasoning } from '../interaction/interaction.model';
import { AnonymizationService } from './anonymization.service';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private readonly http = inject(HttpClient);
  private readonly anonymizationService = inject(AnonymizationService);
  newChatSubject = new ReplaySubject<void>();
  router = inject(Router);
  chatReset$ = this.newChatSubject.asObservable();

  resetChat(index?: string) {
    if (index) {
      this.router.navigate(['/search'], { queryParams: { index } });
    } else {
      this.router.navigate(['/search'], { queryParamsHandling: 'preserve' });
    }
    this.newChatSubject.next();
  }

  search(text_input: string, session_token: string, index?: string) {
    const outboundTextInput = this.anonymizationService.deanonymizeText(text_input);

    const paramsObj: Record<string, string> = {
      text_input: outboundTextInput,
      session_token,
    };
    if (index) {
      paramsObj['index'] = index;
    }

    const options = {
      context: new HttpContext().set(BYPASS_SPINNER, true),
      params: new HttpParams({
        fromObject: paramsObj,
      }),
    };
    return this.http.post(`${environment.api.search}/conversation`, {}, options).pipe(
      map((res: any) => {
        if (!this.anonymizationService.isTargetUser()) {
          return res;
        }
        const divisor = this.anonymizationService.getRandomDivisor();
        const anonymized = { ...res };
        if (Array.isArray(anonymized.response)) {
          anonymized.response = anonymized.response.map((msg: string) =>
            this.anonymizationService.anonymizeText(msg, divisor)
          );
        }
        if (Array.isArray(anonymized.reasoning)) {
          anonymized.reasoning = anonymized.reasoning.map((r: Reasoning) =>
            this.anonymizationService.anonymizeReasoning(r, divisor)
          );
        }
        return anonymized;
      }),
      catchError((err) => {
        console.error('Error during search request:', err); // Log the error
        return of({
          response: ['Errore durante la richiesta'],
          reasoning: [{ agent: `ERROR: ${err.status || 500}`, messages: [`${err.statusText}`] }],
        });
      }),
    );
  }

  streamingSearch(
    text_input: string,
    session_token: string,
    extra_docs: boolean = false,
    index?: string
  ): Observable<any> {
    const outboundTextInput = this.anonymizationService.deanonymizeText(text_input);

    let requestParams = new HttpParams({
      fromObject: {
        text_input: outboundTextInput,
        session_token,
      },
    });

    if (extra_docs) {
      requestParams = requestParams.append('extra_docs', 'true');
    }

    if (index) {
      requestParams = requestParams.append('index', index);
    }

    const req = new HttpRequest(
      'POST',
      `${environment.api.search}/conversation_streaming`,
      {},
      {
        params: requestParams,
        reportProgress: true,
        responseType: 'text',
        context: new HttpContext().set(BYPASS_SPINNER, true),
      },
    );

    const streamDivisor = this.anonymizationService.getRandomDivisor();

    return this.http.request<Reasoning>(req).pipe(
      filter((event) => event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.Response),
      scan((acc, event: any) => (acc + HttpEventType.DownloadProgress ? event.partialText : event.body), ''),
      filter((text) => {
        let jsonString = '';
        try {
          const startNewJson = text.lastIndexOf('}{') + 1;
          jsonString = text.substring(startNewJson);
          JSON.parse(jsonString);

          return true;
        } catch (error) {
          console.log('managed chunk', error);

          return false;
        }
      }),
      map((text: any /* HttpProgressEvent */) => {
        const startNewJson = text.lastIndexOf('}{') + 1;
        const jsonString = text.substring(startNewJson);
        console.log('jsonString->', jsonString.length);

        const json = JSON.parse(jsonString);
        console.log('returning->', json);

        return this.anonymizationService.anonymizeReasoning(json, streamDivisor);
      }),

      catchError((err) => {
        console.error('Error during streaming search request:', err); // Log the error
        // 1. Prova a prendere il messaggio dal body della risposta (BE), se non c'è, usa il messaggio standard.
        const errorMessage = 'Il sistema è momentaneamente sovraccarico a causa dell\'alto traffico. Ci scusiamo per l\'attesa; riprova tra qualche secondo.';
        return of({
          type: 'final_response',
          messages: [`Errore durante la richiesta : ${errorMessage}`],
        });
      }),
    );
  }
}
